'use client'

import { useEffect } from 'react'

import { cn, StickyContainer, useMultiScrollState, useTab } from '@/shared/lib'
import { Tab } from '@/shared/ui'

import { TabValue } from '../../lib/tab'

type Props = {
  tabs: { label: string; value: TabValue }[]
  isMobile?: boolean
}

export const Tabs = ({ tabs, isMobile = false }: Props) => {
  // 모바일 상단 헤더 높이만큼 오프셋
  const offset = isMobile ? 56 : 0

  const {
    activeTab,
    setActiveTab,
    getSectionTop,
    updateSections,
    handleScroll,
  } = useTab<typeof TabValue>({
    defaultValue: TabValue.ROOM_INFORMATION,
    tabs,
    offset,
  })

  const { scroll } = useMultiScrollState()

  const handleTabClick = (tabValue: TabValue) => () => {
    setActiveTab(tabValue)

    const sectionTop = getSectionTop(tabValue)

    // 혹시라도 스크롤 이동 후 이전 섹션 탭이 활성화되지 않도록 1px 오프셋 추가
    scroll(tabValue, { top: sectionTop, offset: 1 })
  }

  useEffect(() => {
    requestAnimationFrame(() => {
      updateSections()
    })

    window.addEventListener('resize', updateSections)

    return () => {
      window.removeEventListener('resize', updateSections)
    }
  }, [updateSections])

  useEffect(() => {
    handleScroll()

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  return (
    <StickyContainer offset={offset} className={cn(isMobile && 'top-14')}>
      <Tab.List
        className={cn(
          isMobile && 'border-gray-50 before:content-none after:content-none',
        )}
      >
        {tabs.map(tab => (
          <Tab.Item
            key={tab.value}
            selected={activeTab === tab.value}
            onClick={handleTabClick(tab.value)}
            className={cn(isMobile && 'typo-label1-reading px-3 first:pl-3')}
          >
            {tab.label}
          </Tab.Item>
        ))}
      </Tab.List>
    </StickyContainer>
  )
}
